import { useEffect, useState, createContext, useContext } from "react";
import { useLocation } from "react-router-dom";

interface SubdomainContextType {
  tenantSlug: string | null;
  isSubdomain: boolean;
}

const SubdomainContext = createContext<SubdomainContextType>({
  tenantSlug: null,
  isSubdomain: false,
});

export const useSubdomain = () => useContext(SubdomainContext);

/**
 * Component that detects tenant subdomains and provides context
 * E.g., rangemore.sitebook.uk -> provides "rangemore" as tenantSlug
 */
export const SubdomainRouter = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const [subdomainInfo, setSubdomainInfo] = useState<SubdomainContextType>({
    tenantSlug: null,
    isSubdomain: false,
  });

  useEffect(() => {
    const hostname = window.location.hostname;
    console.log("[SubdomainRouter] Hostname:", hostname);

    // Check if we're on a subdomain (not main domain or localhost)
    const isLocalhost = hostname === "localhost" || hostname.startsWith("127.0.0.1");
    const isLovableDomain = hostname.endsWith(".lovableproject.com") || hostname.endsWith(".lovable.app");

    // For production subdomains like rangemore.sitebook.uk
    const parts = hostname.split(".");
    const isSubdomain = !isLocalhost && !isLovableDomain && parts.length >= 3;

    console.log("[SubdomainRouter] isLocalhost:", isLocalhost);
    console.log("[SubdomainRouter] isLovableDomain:", isLovableDomain);
    console.log("[SubdomainRouter] parts:", parts);
    console.log("[SubdomainRouter] isSubdomain:", isSubdomain);

    if (isSubdomain) {
      // Extract tenant slug from subdomain (first part before domain)
      const tenantSlug = parts[0];
      
      // Exclude "www" as a valid tenant slug - treat www.sitebook.uk as main domain
      if (tenantSlug.toLowerCase() === "www") {
        console.log("[SubdomainRouter] www subdomain detected, treating as main domain");
        setSubdomainInfo({ tenantSlug: null, isSubdomain: false });
      } else {
        console.log("[SubdomainRouter] Detected tenant slug:", tenantSlug);
        setSubdomainInfo({ tenantSlug, isSubdomain: true });
      }
    } else {
      setSubdomainInfo({ tenantSlug: null, isSubdomain: false });
    }
  }, [location.pathname]);

  return (
    <SubdomainContext.Provider value={subdomainInfo}>
      {children}
    </SubdomainContext.Provider>
  );
};
